import { z } from "zod"

import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import { notebookSchema } from "./notebooksApi"
import type { Notebook, NotebookDocument } from "./notebookSnapshot"

export interface NotebookRevision {
  revision_id: string
  created_at: string
  source: string
  summary: string
  title: string
}
export interface NotebookRevisionDetail extends NotebookRevision {
  document?: NotebookDocument | null
  assets?: Record<string, string>
}
export async function notebookHistory(
  organizationSlug: string,
  workspaceId: string,
  notebookId: string,
  revisionId?: string,
) {
  const query = new URLSearchParams({
    organization: organizationSlug,
    workspace: workspaceId,
  })
  if (revisionId) {
    query.set("revision", revisionId)
  }
  const response = await fetch(
    `/api/sofia/notebooks/${encodeURIComponent(notebookId)}/history?${query}`,
    {
      headers: await getOrganizationBearerHeaders(organizationSlug),
      cache: "no-store",
    },
  )
  if (!response.ok) {
    throw new Error("Could not load notebook history.")
  }
  const data: unknown = await response.json()
  const version = z
    .object({
      revision_id: z.string(),
      created_at: z.string(),
      source: z.string(),
      summary: z.string(),
      title: z.string(),
    })
    .passthrough()
  if (revisionId) {
    return {
      versions: [
        z.object({ version }).parse(data).version as NotebookRevisionDetail,
      ],
    }
  }
  return z
    .object({ versions: z.array(version), truncated: z.boolean().optional() })
    .parse(data) as { versions: NotebookRevisionDetail[]; truncated?: boolean }
}
export async function restoreNotebook(
  organizationSlug: string,
  workspaceId: string,
  notebook: Notebook,
  revisionId: string,
) {
  const response = await fetch(
    `/api/sofia/notebooks/${encodeURIComponent(notebook.id)}/history`,
    {
      method: "POST",
      headers: {
        ...(await getOrganizationBearerHeaders(organizationSlug)),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organization: organizationSlug,
        workspace: workspaceId,
        revision_id: revisionId,
        expected_updated_at: notebook.updated_at,
      }),
    },
  )
  const data: unknown = await response.json()
  if (!response.ok) {
    throw new Error(
      "Could not restore this version. Reload the notebook and try again.",
    )
  }
  return z.object({ notebook: notebookSchema }).parse(data).notebook
}
